import {
  Box,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
} from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs/ClosableDialog'
import { Button } from 'components/facets'
import { RichTextEditorWithGallery } from 'components/facets/RichTextEditorWithGallery'
import {
  createStaticPage,
  deleteStaticPage,
  getStaticPages,
  StaticPage,
  updateStaticPage,
} from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table } from 'react-virtualized'

interface StaticPageFormState {
  url: string
  title: string
  html: string
  htmlEs: string
  htmlFr: string
  htmlDe: string
  htmlIt: string
  draft: boolean
}

const emptyForm: StaticPageFormState = {
  url: '',
  title: '',
  html: '',
  htmlEs: '',
  htmlFr: '',
  htmlDe: '',
  htmlIt: '',
  draft: true,
}

const LANGS = ['en', 'es', 'fr', 'de', 'it'] as const

const htmlKey = (lang: string): keyof StaticPageFormState =>
  lang === 'en'
    ? 'html'
    : (`html${lang.charAt(0).toUpperCase() + lang.slice(1)}` as keyof StaticPageFormState)

export const StaticPagesManager = (): JSX.Element => {
  const [pages, setPages] = useState<StaticPage[]>([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState<StaticPageFormState>(emptyForm)
  const [formVisible, setFormVisible] = useState(true)
  const [langTab, setLangTab] = useState(0)
  const [editLangTab, setEditLangTab] = useState(0)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editForm, setEditForm] = useState<StaticPageFormState>(emptyForm)
  const [editSaving, setEditSaving] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const fetchPages = async () => {
    try {
      const data = await getStaticPages()
      setPages(data)
    } catch {
      setPages([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPages()
  }, [])

  const isBodyEmpty = (html: string) => html.replace(/<[^>]*>/g, '').trim().length === 0

  const isFormValid = (f: StaticPageFormState) =>
    !!f.url.trim() &&
    !!f.title.trim() &&
    !isBodyEmpty(f.html) &&
    !isBodyEmpty(f.htmlEs) &&
    !isBodyEmpty(f.htmlFr) &&
    !isBodyEmpty(f.htmlDe) &&
    !isBodyEmpty(f.htmlIt)

  const handleSubmit = async () => {
    if (!isFormValid(form)) return
    setSaving(true)
    try {
      await createStaticPage(form)
      enqueueSnackbar(t('staticPages.createSuccess'), { variant: 'success' })
      setFormVisible(false)
      setForm(emptyForm)
      setLangTab(0)
      await fetchPages()
      setFormVisible(true)
    } catch {
      enqueueSnackbar(t('staticPages.createFailed'), { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (page: StaticPage) => {
    setEditingId(page.id)
    setEditForm({
      url: page.url,
      title: page.title || '',
      html: page.html || '',
      htmlEs: page.htmlEs || '',
      htmlFr: page.htmlFr || '',
      htmlDe: page.htmlDe || '',
      htmlIt: page.htmlIt || '',
      draft: page.draft,
    })
    setEditDialogOpen(true)
  }

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false)
    setEditingId(null)
    setEditForm(emptyForm)
    setEditLangTab(0)
  }

  const handleEditSubmit = async () => {
    if (!editingId || !isFormValid(editForm)) return
    setEditSaving(true)
    try {
      await updateStaticPage(editingId, editForm)
      enqueueSnackbar(t('staticPages.updateSuccess'), { variant: 'success' })
      handleCloseEditDialog()
      await fetchPages()
    } catch {
      enqueueSnackbar(t('staticPages.updateFailed'), { variant: 'error' })
    } finally {
      setEditSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('staticPages.deleteConfirm'))) return
    try {
      await deleteStaticPage(id)
      enqueueSnackbar(t('staticPages.deleteSuccess'), { variant: 'success' })
      await fetchPages()
    } catch {
      enqueueSnackbar(t('staticPages.deleteFailed'), { variant: 'error' })
    }
  }

  // Opens the authenticated preview (drafts included) in a new tab, reusing the public template.
  const openPreview = (id: number, lang: string) =>
    window.open(`/static_pages_preview/${id}/${lang}`, '_blank', 'noopener,noreferrer')

  const langTabs = (active: number, onSelect: (i: number) => void, testidPrefix: string) => (
    <ul className="nav nav-tabs">
      {LANGS.map((l, i) => (
        <li key={l} className="nav-item">
          <a
            className={`nav-link${active === i ? ' active' : ''}`}
            href="#"
            data-testid={`${testidPrefix}-${l}`}
            onClick={(e) => {
              e.preventDefault()
              onSelect(i)
            }}
          >
            {l.toUpperCase()}
          </a>
        </li>
      ))}
    </ul>
  )

  return (
    <div className="p-3">
      <h4 className="mb-3">{t('staticPages.title')}</h4>

      <div className="my-5">
        <h6 className="m-0 p-0 mb-2">{t('staticPages.newPage')}</h6>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              label={t('staticPages.url')}
              size="small"
              fullWidth
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              placeholder={t('staticPages.urlPlaceholder')}
              inputProps={{ 'data-testid': 'static-page-url-input' }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.draft}
                  onChange={(e) => setForm((f) => ({ ...f, draft: e.target.checked }))}
                  inputProps={{ 'data-testid': 'static-page-draft-toggle' } as any}
                />
              }
              label={t('staticPages.draft')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('staticPages.pageTitle')}
              size="small"
              fullWidth
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              inputProps={{ 'data-testid': 'static-page-title-input' }}
            />
          </Grid>
          <Grid item xs={12}>
            {langTabs(langTab, setLangTab, 'static-page-lang')}
            {formVisible && (
              <Box key={`create-${LANGS[langTab]}`} className="localized-content-form">
                <FormLabel className="mb-3">{t('staticPages.body')}</FormLabel>
                <RichTextEditorWithGallery
                  value={form[htmlKey(LANGS[langTab])] as string}
                  onChange={(value) => setForm((f) => ({ ...f, [htmlKey(LANGS[langTab])]: value }))}
                  placeholder={t('staticPages.body')}
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSubmit}
              disabled={saving || !isFormValid(form)}
              data-testid="static-page-create-btn"
            >
              {saving ? t('staticPages.saving') : t('staticPages.create')}
            </Button>
          </Grid>
        </Grid>
      </div>

      {loading ? (
        <span className="small text-muted">{t('common.loading')}</span>
      ) : pages.length === 0 ? (
        <span className="small text-muted">{t('staticPages.noPages')}</span>
      ) : (
        <div style={{ height: Math.min(pages.length * 45 + 40 + 30, 450) }}>
          <span className="mb-1">{t('staticPages.pagesCount', { count: pages.length })}</span>
          <AutoSizer>
            {({ width }) => (
              <Table
                width={width}
                height={400}
                headerHeight={40}
                rowHeight={45}
                rowCount={pages.length}
                rowGetter={({ index }) => pages[index]}
              >
                <Column
                  width={width * 0.37}
                  label={t('staticPages.url')}
                  dataKey="url"
                  cellRenderer={({ rowData }) => (
                    <span
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        fontFamily: 'monospace',
                      }}
                    >
                      /{rowData.url}
                    </span>
                  )}
                />
                <Column
                  width={width * 0.3}
                  label={t('staticPages.pageTitle')}
                  dataKey="title"
                  cellRenderer={({ rowData }) => (
                    <span
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                      }}
                    >
                      {rowData.title}
                    </span>
                  )}
                />
                <Column
                  width={width * 0.13}
                  label={t('staticPages.status')}
                  dataKey="draft"
                  cellRenderer={({ rowData }) => (
                    <span>
                      {rowData.draft ? t('staticPages.draft') : t('staticPages.published')}
                    </span>
                  )}
                />
                <Column
                  width={width * 0.2}
                  label={t('staticPages.actions')}
                  dataKey="id"
                  disableSort
                  cellRenderer={({ rowData }) => (
                    <>
                      <IconButton
                        size="small"
                        title={t('staticPages.preview')}
                        onClick={() => openPreview(rowData.id, 'en')}
                        data-testid="static-page-preview-btn"
                      >
                        <i className="fa-light fa-eye" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(rowData)}
                        data-testid="static-page-edit-btn"
                      >
                        <i className="fa-light fa-pen-to-square" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(rowData.id)}
                        data-testid="static-page-delete-btn"
                      >
                        <i className="fa-light fa-trash" />
                      </IconButton>
                    </>
                  )}
                />
              </Table>
            )}
          </AutoSizer>
        </div>
      )}

      <ClosableDialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="lg" fullWidth>
        <h3 className="mb-3">{t('staticPages.editPage')}</h3>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              label={t('staticPages.url')}
              size="small"
              fullWidth
              value={editForm.url}
              onChange={(e) => setEditForm((f) => ({ ...f, url: e.target.value }))}
              placeholder={t('staticPages.urlPlaceholder')}
              inputProps={{ 'data-testid': 'static-page-edit-url-input' }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.draft}
                  onChange={(e) => setEditForm((f) => ({ ...f, draft: e.target.checked }))}
                />
              }
              label={t('staticPages.draft')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('staticPages.pageTitle')}
              size="small"
              fullWidth
              value={editForm.title}
              onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              inputProps={{ 'data-testid': 'static-page-edit-title-input' }}
            />
          </Grid>
          <Grid item xs={12}>
            {langTabs(editLangTab, setEditLangTab, 'static-page-edit-lang')}
            <Box key={`edit-${LANGS[editLangTab]}`} className="localized-content-form">
              <FormLabel className="mb-3">{t('staticPages.body')}</FormLabel>
              <RichTextEditorWithGallery
                value={editForm[htmlKey(LANGS[editLangTab])] as string}
                onChange={(value) =>
                  setEditForm((f) => ({ ...f, [htmlKey(LANGS[editLangTab])]: value }))
                }
                placeholder={t('staticPages.body')}
              />
            </Box>
          </Grid>
        </Grid>
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button size="small" variant="outlined" onClick={handleCloseEditDialog} className="mr-2">
            {t('common.cancel')}
          </Button>
          <Button
            className="ms-2"
            variant="contained"
            color="primary"
            size="small"
            onClick={handleEditSubmit}
            disabled={editSaving || !isFormValid(editForm)}
            data-testid="static-page-edit-save-btn"
          >
            {editSaving ? t('staticPages.saving') : t('common.save')}
          </Button>
        </Box>
      </ClosableDialog>
    </div>
  )
}
